import { PauseOutlined, CaretRightOutlined } from '@ant-design/icons';
import { ConfigProvider, Flex } from 'antd';
import classnames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CameraMode from './CameraMode';
import Cameras from './Cameras';
import RecordTimer from './RecordTimer';
import { canGenerateVideo } from '../utils';
import type { CameraModeEnum } from './types';
import { BannerUI, ButtonSpacer, Option, PauseButton, Record } from './UI';

type BannerProps = {
  disabled: boolean;
  mode: CameraModeEnum;
  onPause?: () => void;
  onResume?: () => void;
  onCapture?: () => void;
  onCameraChange?: () => void;
  onStopRecording?: () => void;
  showCamerasSwitcher?: boolean;
  onStartRecording?: () => void;
  recordingState: RecordingState;
  onModeChange?: (mode: CameraModeEnum) => void;
};

const Banner: React.FC<BannerProps> = ({
  mode,
  disabled,
  recordingState,
  onPause = () => {},
  onResume = () => {},
  onCapture = () => {},
  onModeChange = () => {},
  onCameraChange = () => {},
  onStopRecording = () => {},
  onStartRecording = () => {},
  showCamerasSwitcher = false,
}) => {
  const intl = useIntl();
  const isVideo = mode === 'video';
  const isPaused = recordingState === 'paused';
  const isRecording = recordingState !== 'inactive';
  const showSwitch = showCamerasSwitcher && !isRecording;

  const isActive = (cameraMode: CameraModeEnum) => cameraMode === mode;

  const buttonProps = React.useMemo(() => ({
    'data-cy': isPaused ? 'camera-resume-btn' : 'camera-pause-btn',
    title: intl.formatMessage(
      isPaused
        ? { id: 'camera.resume', defaultMessage: 'Resume' }
        : { id: 'camera.pause', defaultMessage: 'Pause' }
    ),
    icon: isPaused ? <CaretRightOutlined /> : <PauseOutlined />,
    onClick: isPaused ? onResume : onPause,
  }), [isPaused, onPause, onResume, intl]);

  const handleOnClick = () => {
    if (disabled) {
      return;
    }

    if (isVideo) {
      if (!isRecording) {
        onStartRecording();
        return;
      }
      onStopRecording();
      return;
    }
    onCapture();
  };

  return (
    <BannerUI className={classnames({ disabled })}>
      {showSwitch && <Cameras onChange={onCameraChange} />}
      {!isRecording && (
        <Option>
          <CameraMode isActive={isActive('picture')} mode="picture" onChange={onModeChange}>
            <FormattedMessage id="global.photo" defaultMessage="Photo" />
          </CameraMode>
          {canGenerateVideo() && (
            <CameraMode isActive={isActive('video')} mode="video" onChange={onModeChange}>
              <FormattedMessage id="global.video" defaultMessage="Video" />
            </CameraMode>
          )}
        </Option>
      )}
      {isRecording && <RecordTimer isPaused={isPaused} />}
      <Flex gap={12} align="center" justify="center">
        {isRecording && <ButtonSpacer />}
        <Record
          className={classnames({ movie: isVideo, recording: isRecording, disabled })}
          onClick={handleOnClick}
          data-cy="camera-record"
        />
        {isRecording && (
          <ConfigProvider theme={{ components: { Button: { controlHeight: 40 } } }}>
            <PauseButton
              shape="circle"
              {...buttonProps}
            />
          </ConfigProvider>
        )}
      </Flex>
    </BannerUI>
  );
};

export default Banner;
